import Chip from '@mui/material/Chip';
import { formatAmount } from 'modules/shared/utils';
import type { PayrollRow, SalaryRelease } from '../hr.api';

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** `{ year, month }` where month is 1-12, defaulting to the month just finished — the one payroll runs for. */
export function lastCompletedMonth(): { year: number; month: number } {
  const now = new Date();
  const month = now.getMonth(); // 0-11 for *this* month == 1-12 for the previous one
  return month === 0
    ? { year: now.getFullYear() - 1, month: 12 }
    : { year: now.getFullYear(), month };
}

export const monthLabel = (year: number, month: number) => `${MONTHS[month - 1]} ${year}`;

export const money = (value: unknown) =>
  value === null || value === undefined || value === '' ? '—' : formatAmount(Number(value));

/** Hours read better with one decimal and no currency dressing. */
export const hours = (value: unknown) =>
  value === null || value === undefined || value === '' ? '—' : Number(value).toFixed(1);

export const days = (value: unknown) =>
  value === null || value === undefined || value === '' ? '—' : String(value);

/** Where a month stands, as a colour the board can be read at a glance by. */
export function releaseChipColor(status: string | undefined) {
  switch ((status ?? '').toLowerCase()) {
    case 'paid':
      return 'success' as const;
    case 'requested':
      return 'warning' as const;
    default:
      return 'default' as const;
  }
}

export const verifiedChip = (value: unknown) => (
  <Chip
    size='small'
    label={value ? 'Verified' : 'Pending'}
    color={value ? 'success' : 'default'}
    variant={value ? 'filled' : 'outlined'}
  />
);

/** True once the month is settled — every payroll screen goes read-only from here. */
export const isMonthLocked = (release: SalaryRelease | null) =>
  (release?.status ?? '').toLowerCase() === 'paid';

/** The month's cost, summed from the rows on screen so the total always matches them. */
export const monthTotal = (rows: PayrollRow[]) =>
  rows.reduce((sum, row) => sum + Number(row.net_payable ?? 0), 0);

export const unverifiedCount = (rows: PayrollRow[]) =>
  rows.filter((row) => !row.verified_by_hr).length;
